import { normalize } from "common/lib/normalize";
import { getSortObj } from "common/lib/tableDataOptions";
import { getGovtRequests } from "common/services/govt-requests.service";
import Permission from "Components/Common/Permission";
import TableContainer from "Components/Common/TableContainer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";

const GovernmentRequestModal = ({ isOpen, toggle, filter, title }: any) => {
  const [params] = useState({
    page: 1, // Starts from 1
    pageSize: 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() => {
    return [{ id: "createdAt", desc: true }];
  });

  const [filterValue, setFilterValue] = useState(() => {
    return {
      positionType: filter?.positionType || null,
      agency: filter?.agency || null,
      mda: filter?.mda || null,
      dateRange: filter?.dateRange || null,
    };
  });

  useEffect(() => {
    setFilterValue({
      positionType: filter?.positionType || null,
      agency: filter?.agency || null,
      mda: filter?.mda || null,
      dateRange: filter?.dateRange || null,
    });
  }, [filter]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.mda?.value) {
        andConditions.push({
          mda: {
            id: {
              $eq: filterValue.mda.value,
            },
          },
        });
      }

      if (filterValue?.agency) {
        andConditions.push({
          agency: {
            id: {
              $eq: filterValue.agency.value,
            },
          },
        });
      }
      if (filterValue?.positionType) {
        andConditions.push({
          positionType: {
            id: {
              $eq: filterValue.positionType.value,
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        andConditions.push({
          $or: [
            {
              startDate: {
                $between: [
                  filterValue.dateRange.fromDate,
                  filterValue.dateRange.toDate,
                ],
              },
            },
            {
              endDate: {
                $between: [
                  filterValue.dateRange.fromDate,
                  filterValue.dateRange.toDate,
                ],
              },
            },
          ],
        });
      }

      andConditions.push({
        // $or: [
        //   {
        //     individual: {
        //       fullName: {
        //         $containsi: searchValue,
        //       },
        //     },
        //   },
        // ],
      });

      try {
        setLoading(true);

        const response: any = await getGovtRequests({
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "agency",
            "mda",
            "mda.region",
            "individual",
            "positionType",
            "typeOfRequest",
          ],
          filters: {
            $and: andConditions,
          },
        });
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  const columns = useMemo(
    () => [
      {
        header: "Ministry/Department/Agency (MDA)",
        accessorKey: "mda.name",
        id: "mda.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  // whiteSpace: "pre-wrap",
                  width: "400px",
                }}
              >
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title bg-primary-subtle text-danger rounded fs-24">
                      <i className="ri-bank-line text-primary"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5 className="fs-14 mb-1" style={{ whiteSpace: "pre-wrap" }}>
                    <Permission
                      alternative={
                        <Link
                          to={`/government-requests/${rowData.id}`}
                          className="text-body"
                        >
                          {cell.getValue()}
                        </Link>
                      }
                      action="findOne"
                      resource="mda"
                    >
                      <Link
                        to={`/government-requests/${rowData.id}`}
                        className="text-body"
                      >
                        {cell.getValue()}
                      </Link>
                    </Permission>
                  </h5>
                  <p className="text-muted mb-0">
                    <span className="fw-medium">
                      {cell.row.original?.mda?.region?.name || ""}
                    </span>
                  </p>
                </div>
              </div>
            </>
          );
        },
      },
      {
        header: "Type of Request",
        accessorKey: "typeOfRequest.name",
        enableColumnFilter: false,
        id: "typeOfRequest.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },
      {
        header: "Period",
        accessorKey: "periodInMonths",
        enableColumnFilter: false,
        id: "periodInMonths",
        cell: (cell: any) => {
          const data = cell.getValue();
          if (!data) return "Not Provided";
          const label = data === 1 ? "Month" : "Months";
          return (
            <>
              <h5 className="fs-14 my-1 fw-normal">{data}</h5>
              <span className="text-muted">{label}</span>
            </>
          );
        },
      },

      {
        header: "Reporting Agency",
        accessorKey: "agency.name",
        enableColumnFilter: false,
        id: "agency.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },

      {
        header: "Funding Status",
        accessorKey: "fundingStatus",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return cell.getValue() || "Not Provided";
        },
      },
      {
        header: "Value of Request",
        accessorKey: "totalValueOfRequest",
        enableColumnFilter: false,
        id: "totalValueOfRequest",
        cell: (cell: any) => {
          return (
            <>
              {cell.getValue()
                ? `$ ${cell.getValue().toLocaleString()}`
                : "Not Provided"}
            </>
          );
        },
      },
    ],
    []
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      style={{ maxWidth: "95%", width: "95%", margin: "20px auto" }}
    >
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        <Card>
          <CardHeader>
            <Row className="align-items-center g-3">
              <Col md={6}>
                <h5 className="card-title mb-1">Government Requests</h5>
                <p className="mb-0 text-muted">
                  A list of government requests made to UN Agencies from
                  ministries, departments and agencies.
                </p>
              </Col>
              <div className="col-md-auto ms-auto">
                <div className="d-flex gap-2"></div>
              </div>
            </Row>
          </CardHeader>
          <CardBody>
            <TableContainer
              loading={loading}
              columns={columns}
              data={data || []}
              pagination={pagination}
              setPagination={setPagination}
              sorted={sorted}
              setSorted={setSorted}
              onPageChange={onPageChange}
              FilterSection={() => {}}
              totalRows={meta?.pagination?.total || 0}
              totalPages={meta?.pagination?.pageCount || 0}
              filterValue={filterValue}
              divClass="table-responsive table-card mb-4"
              tableClass="align-middle table-nowrap table-striped mb-0"
              theadClass="table-light table-nowrap"
            />
          </CardBody>
        </Card>
      </ModalBody>
    </Modal>
  );
};

export default GovernmentRequestModal;
